import React, { useState } from 'react';
import { ArrowDown, ArrowUp } from 'lucide-react';
import DifficultyGauge from '../DifficultyGauge';
import { PlayerStats } from './types';

interface Props {
  rows: PlayerStats[];
}

type SortKey =
  | 'display_name'
  | 'matches'
  | 'matches_won'
  | 'matches_lost'
  | 'match_win_pct'
  | 'sets_won'
  | 'sets_lost'
  | 'set_win_pct'
  | 'games_won'
  | 'games_lost'
  | 'game_win_pct'
  | 'dynamic_rating'
  | 'avg_opponent_dynamic_rating';

interface Column {
  key: SortKey;
  label: string;
  align: 'left' | 'right' | 'center';
  // Plain-language explanation of the column, surfaced as a hover tooltip on
  // the header so abbreviations (Match %, Dynamic Rating, …) are self-explaining.
  tooltip: string;
  isPct?: boolean;
  // Visual group: separator drawn to the LEFT of the column when group changes
  group: 'player' | 'matches' | 'sets' | 'games' | 'difficulty';
  render?: (row: PlayerStats) => React.ReactNode;
  // Override the value used when sorting this column. Defaults to row[key].
  // Needed when the displayed value is derived rather than the raw field — e.g.
  // the relative-strength gauge shows opponent rating *relative* to the player's
  // own rating, so it must sort on that difference, not the raw opponent rating.
  sortValue?: (row: PlayerStats) => number | string | null;
  // Show this column only when the predicate holds for the current rows.
  // Columns without it are always shown.
  hasData?: (rows: PlayerStats[]) => boolean;
}

const COLUMNS: Column[] = [
  {
    key: 'display_name',
    label: 'Player',
    align: 'left',
    group: 'player',
    tooltip: 'Team member name. Click any column header to sort by it.',
  },
  {
    key: 'matches',
    label: 'Matches',
    align: 'right',
    group: 'matches',
    tooltip: 'Total matches played.',
  },
  {
    key: 'matches_won',
    label: 'Won',
    align: 'right',
    group: 'matches',
    tooltip: 'Matches won.',
  },
  {
    key: 'matches_lost',
    label: 'Lost',
    align: 'right',
    group: 'matches',
    tooltip: 'Matches lost.',
  },
  {
    key: 'match_win_pct',
    label: 'Match %',
    align: 'right',
    isPct: true,
    group: 'matches',
    tooltip: 'Match win rate — matches won ÷ matches played.',
  },
  {
    key: 'sets_won',
    label: 'Sets won',
    align: 'right',
    group: 'sets',
    tooltip: 'Total sets won across all matches.',
  },
  {
    key: 'sets_lost',
    label: 'Sets lost',
    align: 'right',
    group: 'sets',
    tooltip: 'Total sets lost across all matches.',
  },
  {
    key: 'set_win_pct',
    label: 'Set %',
    align: 'right',
    isPct: true,
    group: 'sets',
    tooltip: 'Set win rate — sets won ÷ sets played.',
  },
  {
    key: 'games_won',
    label: 'Games won',
    align: 'right',
    group: 'games',
    tooltip: 'Total games won across all matches.',
  },
  {
    key: 'games_lost',
    label: 'Games lost',
    align: 'right',
    group: 'games',
    tooltip: 'Total games lost across all matches.',
  },
  {
    key: 'game_win_pct',
    label: 'Game %',
    align: 'right',
    isPct: true,
    group: 'games',
    tooltip: 'Game win rate — games won ÷ games played.',
  },
  {
    key: 'dynamic_rating',
    label: 'Dynamic Rating',
    align: 'right',
    group: 'difficulty',
    tooltip: "The player's estimated dynamic skill rating (e.g. 3.50). Higher is stronger.",
    hasData: (rows) => rows.some((r) => r.dynamic_rating != null),
    render: (row) =>
      row.dynamic_rating != null ? (
        row.dynamic_rating.toFixed(2)
      ) : (
        <span className="text-gray-400 dark:text-gray-500">—</span>
      ),
  },
  {
    key: 'avg_opponent_dynamic_rating',
    label: 'Relative Strength',
    align: 'center',
    group: 'difficulty',
    tooltip:
      "Average strength of the opponents this player faced, relative to the player's own dynamic rating. Needle right (orange) = tougher opponents; left (green) = easier.",
    hasData: (rows) =>
      rows.some((r) => r.avg_opponent_dynamic_rating != null && r.dynamic_rating != null),
    // Sort on the opponent-vs-player delta the gauge renders, not the raw
    // opponent rating (each row's baseline is its own dynamic_rating).
    sortValue: (row) =>
      row.avg_opponent_dynamic_rating != null && row.dynamic_rating != null
        ? row.avg_opponent_dynamic_rating - row.dynamic_rating
        : null,
    render: (row) =>
      row.avg_opponent_dynamic_rating != null && row.dynamic_rating != null ? (
        <DifficultyGauge
          teamAvg={row.dynamic_rating}
          opponentAvg={row.avg_opponent_dynamic_rating}
        />
      ) : (
        <span className="text-gray-400 dark:text-gray-500">—</span>
      ),
  },
];

const isGroupBoundary = (cols: Column[], i: number): boolean =>
  i > 0 && cols[i].group !== cols[i - 1].group;

const PlayerLeaderboard: React.FC<Props> = ({ rows }) => {
  const [sortKey, setSortKey] = useState<SortKey>('matches_won');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  if (rows.length === 0) {
    return (
      <p className="text-sm text-gray-500 dark:text-gray-400 italic">No player results yet.</p>
    );
  }

  const columns = COLUMNS.filter((c) => !c.hasData || c.hasData(rows));

  const sortColumn = COLUMNS.find((c) => c.key === sortKey);
  const sortValueOf = (row: PlayerStats) =>
    sortColumn?.sortValue ? sortColumn.sortValue(row) : row[sortKey];

  const sorted = [...rows].sort((a, b) => {
    const av = sortValueOf(a);
    const bv = sortValueOf(b);
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    if (typeof av === 'string' && typeof bv === 'string') {
      return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    }
    return sortDir === 'asc' ? Number(av) - Number(bv) : Number(bv) - Number(av);
  });

  const toggleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'display_name' ? 'asc' : 'desc');
    }
  };

  const alignClass = (align: 'left' | 'right' | 'center'): string =>
    align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left';

  return (
    <div className="overflow-auto max-h-96" data-testid="player-leaderboard">
      <table className="min-w-full text-sm whitespace-nowrap">
        <thead>
          <tr>
            {columns.map((col, i) => (
              <th
                key={col.key}
                onClick={() => toggleSort(col.key)}
                title={col.tooltip}
                className={`sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 py-2 px-2.5 font-semibold text-gray-700 dark:text-gray-200 cursor-pointer select-none hover:text-primary-600 dark:hover:text-primary-400 ${alignClass(col.align)} ${
                  isGroupBoundary(columns, i)
                    ? 'border-l border-gray-100 dark:border-gray-700/60'
                    : ''
                } ${
                  i === 0
                    ? 'left-0 z-30 border-r border-gray-200 dark:border-gray-700'
                    : 'z-20'
                }`}
                aria-sort={
                  sortKey === col.key ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'
                }
              >
                <span className="inline-flex items-center gap-1">
                  {col.label}
                  {sortKey === col.key &&
                    (sortDir === 'asc' ? (
                      <ArrowUp className="w-3 h-3" />
                    ) : (
                      <ArrowDown className="w-3 h-3" />
                    ))}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((row, rowIdx) => (
            <tr
              key={row.user_id}
              className={`group hover:bg-gray-100 dark:hover:bg-gray-700/40 ${
                rowIdx % 2 === 1 ? 'bg-gray-50/70 dark:bg-gray-700/20' : ''
              }`}
            >
              {columns.map((col, i) => {
                const cellClass = `py-1.5 px-2.5 ${alignClass(col.align)} ${
                  col.align === 'left'
                    ? 'text-gray-900 dark:text-gray-100'
                    : 'text-gray-700 dark:text-gray-300'
                } ${isGroupBoundary(columns, i) ? 'border-l border-gray-100 dark:border-gray-700/50' : ''} ${
                  i === 0
                    ? 'sticky left-0 z-10 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 group-hover:bg-gray-100 dark:group-hover:bg-gray-700/40'
                    : ''
                }`;

                if (col.render) {
                  return (
                    <td key={col.key} className={cellClass}>
                      <div className={col.align === 'center' ? 'flex justify-center' : ''}>
                        {col.render(row)}
                      </div>
                    </td>
                  );
                }

                const value = row[col.key];
                const display =
                  col.isPct && typeof value === 'number'
                    ? `${value.toFixed(1)}%`
                    : value == null
                    ? ''
                    : String(value);
                return (
                  <td key={col.key} className={cellClass}>
                    {display}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default PlayerLeaderboard;
